import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Trophy,
  Calendar,
  MapPin,
  Search,
  Hash,
  Target,
  Award,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Loader2,
  FileWarning,
  Filter,
  X
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';
import { useApi } from '../../contexts/ApiContext';

function StatusDot({ filled, total }) {
  if (total === 0) return <span className="w-2.5 h-2.5 rounded-full bg-slate-200 inline-block" />;
  if (filled === total) return <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />;
  if (filled === 0) return <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />;
  return <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />;
}

function FractionCell({ filled, total }) {
  if (total === 0) return <span className="text-slate-300">&mdash;</span>;
  const isComplete = filled === total;
  const isEmpty = filled === 0;
  return (
    <span className={`font-mono text-sm ${isComplete ? 'text-emerald-600' : isEmpty ? 'text-red-500 font-medium' : 'text-amber-600'}`}>
      {filled}/{total}
    </span>
  );
}

function HealthCard({ icon: Icon, label, count, color, active, onClick }) {
  const colorMap = {
    red: { bg: 'bg-red-50', iconBg: 'bg-red-100', iconColor: 'text-red-600', text: 'text-red-700', ring: 'ring-red-300' },
    amber: { bg: 'bg-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', text: 'text-amber-700', ring: 'ring-amber-300' },
    emerald: { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', text: 'text-emerald-700', ring: 'ring-emerald-300' },
    blue: { bg: 'bg-blue-50', iconBg: 'bg-blue-100', iconColor: 'text-blue-600', text: 'text-blue-700', ring: 'ring-blue-300' },
    slate: { bg: 'bg-slate-50', iconBg: 'bg-slate-100', iconColor: 'text-slate-600', text: 'text-slate-700', ring: 'ring-slate-300' },
  };
  const c = colorMap[color] || colorMap.slate;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border transition-all ${
        active ? `${c.bg} border-transparent ring-2 ${c.ring}` : 'bg-white border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${c.iconBg} flex items-center justify-center`}>
          <Icon className={`w-4.5 h-4.5 ${c.iconColor}`} />
        </div>
        <div>
          <div className={`text-2xl font-bold ${active ? c.text : 'text-slate-800'}`}>{count}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </div>
    </button>
  );
}

function SortHeader({ label, sortKey, currentSort, currentOrder, onSort, className = '' }) {
  const isActive = currentSort === sortKey;
  return (
    <button
      onClick={() => onSort(sortKey)}
      className={`flex items-center gap-1 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700 ${className}`}
    >
      {label}
      {isActive ? (
        currentOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40" />
      )}
    </button>
  );
}

export default function Results() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;
  const seriesDbId = currentSeries?.dbId;
  const { series: seriesApi } = useApi();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchHealth = useCallback(async (year, filter, search, sort, order) => {
    if (!seriesDbId) return;
    setLoading(true);
    const params = {};
    if (year) params.year = year;
    if (filter) params.filter = filter;
    if (search) params.search = search;
    if (sort) params.sort = sort;
    if (order) params.order = order;

    const result = await seriesApi.getResultsHealth(seriesDbId, params);
    if (result.success) {
      setData(result.data);
      if (!year && result.data.year) {
        setSelectedYear(String(result.data.year));
      }
    }
    setLoading(false);
  }, [seriesDbId, seriesApi]);

  useEffect(() => {
    fetchHealth(selectedYear, activeFilter, searchQuery, sortKey, sortOrder);
  }, [selectedYear, activeFilter, searchQuery, sortKey, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder(key === 'date' || key === 'name' ? 'asc' : 'desc');
    }
  };

  const handleFilterClick = (filterName) => {
    setActiveFilter(prev => prev === filterName ? null : filterName);
  };

  const health = data?.health || {};
  const events = data?.events || [];
  const availableYears = data?.availableYears || [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Results Data Manager</h1>
            <p className="text-sm text-slate-500">Audit and fix race result data</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="w-28"
          >
            {availableYears.map(yr => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </Select>
        </div>
      </div>

      {/* Data Health Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <HealthCard
          icon={Calendar}
          label="Total Events"
          count={health.totalEvents || 0}
          color="blue"
          active={false}
          onClick={() => handleFilterClick(null)}
        />
        <HealthCard
          icon={FileWarning}
          label="No Results"
          count={health.eventsWithNoResults || 0}
          color={health.eventsWithNoResults > 0 ? 'red' : 'emerald'}
          active={activeFilter === 'no_results'}
          onClick={() => handleFilterClick('no_results')}
        />
        <HealthCard
          icon={Hash}
          label="Missing Cars"
          count={health.missingCarNumbers || 0}
          color={health.missingCarNumbers > 0 ? 'amber' : 'emerald'}
          active={activeFilter === 'missing_cars'}
          onClick={() => handleFilterClick('missing_cars')}
        />
        <HealthCard
          icon={Target}
          label="Missing Positions"
          count={health.missingPositions || 0}
          color={health.missingPositions > 0 ? 'amber' : 'emerald'}
          active={activeFilter === 'missing_positions'}
          onClick={() => handleFilterClick('missing_positions')}
        />
        <HealthCard
          icon={Award}
          label="Missing Points"
          count={health.missingPoints || 0}
          color={health.missingPoints > 0 ? 'amber' : 'emerald'}
          active={activeFilter === 'missing_points'}
          onClick={() => handleFilterClick('missing_points')}
        />
        <HealthCard
          icon={DollarSign}
          label="Missing Winnings"
          count={health.missingWinnings || 0}
          color={health.missingWinnings > 0 ? 'amber' : 'emerald'}
          active={activeFilter === 'missing_winnings'}
          onClick={() => handleFilterClick('missing_winnings')}
        />
      </div>

      {/* Search & Active Filter */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search events, tracks..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10"
              />
            </form>
            {activeFilter && (
              <button
                onClick={() => setActiveFilter(null)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm font-medium border border-amber-200 hover:bg-amber-100 transition-colors"
              >
                <Filter className="w-3.5 h-3.5" />
                {activeFilter.replace(/_/g, ' ')}
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <div className="text-sm text-slate-500">
              {events.length} event{events.length !== 1 ? 's' : ''}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Work Queue Table */}
      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="w-8 h-8 text-slate-400 mx-auto mb-4 animate-spin" />
            <p className="text-sm text-slate-500">Loading health data...</p>
          </CardContent>
        </Card>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              {activeFilter ? 'No events match this filter' : 'No events found'}
            </h3>
            <p className="text-sm text-slate-500">
              {activeFilter ? 'Try a different filter or check another year' : 'Select a different year'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left">
                    <SortHeader label="Date" sortKey="date" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 text-left">
                    <SortHeader label="Event" sortKey="name" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                  </th>
                  <th className="px-4 py-3 text-left">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Track</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Entries</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Results</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cars</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Positions</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Points</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Winnings</span>
                  </th>
                  <th className="px-4 py-3 text-center">
                    <SortHeader label="Issues" sortKey="issues" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} className="justify-center" />
                  </th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {events.map((event, idx) => {
                  const tc = event.total_competitors;
                  const hasIssues = event.issue_count > 0;
                  return (
                    <tr
                      key={event.event_id}
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                    >
                      <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                        {formatDate(event.event_date)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 text-sm">{event.event_name || 'Unnamed Event'}</span>
                          {event.status === 'Cancelled' && (
                            <span className="px-1.5 py-0.5 rounded text-2xs font-semibold bg-red-100 text-red-700 border border-red-200 uppercase">Cancelled</span>
                          )}
                          {event.status === 'Rescheduled' && (
                            <span className="px-1.5 py-0.5 rounded text-2xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 uppercase">Rescheduled</span>
                          )}
                          {event.status === 'Pending Reschedule' && (
                            <span className="px-1.5 py-0.5 rounded text-2xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 uppercase">Pending</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-600">{event.track_name}</div>
                        {(event.city || event.state) && (
                          <div className="text-xs text-slate-400">{[event.city, event.state].filter(Boolean).join(', ')}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-medium text-slate-700">
                        {event.total_entries || 0}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusDot filled={tc} total={event.total_entries || tc} />
                          <FractionCell filled={tc} total={event.total_entries || tc} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusDot filled={event.car_numbers_filled} total={tc} />
                          <FractionCell filled={event.car_numbers_filled} total={tc} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusDot filled={event.positions_filled} total={tc} />
                          <FractionCell filled={event.positions_filled} total={tc} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusDot filled={event.points_filled} total={tc} />
                          <FractionCell filled={event.points_filled} total={tc} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <StatusDot filled={event.winnings_filled} total={tc} />
                          <FractionCell filled={event.winnings_filled} total={tc} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hasIssues ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-bold">
                            <AlertTriangle className="w-3 h-3" />
                            {event.issue_count}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-medium">
                            <CheckCircle2 className="w-3 h-3" />
                            OK
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/${seriesSlug}/events/${event.event_id}/results`}
                          className="inline-flex items-center gap-1 text-xs text-wrg-blue-600 hover:text-wrg-blue-800 font-medium"
                        >
                          Edit <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
